/*
 * $Id:: guards.js 2020/11/11 12:46 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { me as meSlice } from '../pages/AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from '../pages/AuthenticatedPage/constants';

// Helper guard functions.

const canAccessRouter = (Component, { isAllowed, redirectionPath }) => (props) =>
    isAllowed ? <Component {...props} /> : <Redirect to={{ pathname: redirectionPath || '/' }} />;

const accessCheckRender = (Component, userType, authorities, canAccess) =>
    !!canAccess ? canAccessRouter(Component, canAccess(userType, authorities)) : (props) => <Component {...props} />;

const guardedRender = (allowedComponent, guard, redirectionPath) => (props) =>
    guard ? allowedComponent(props) : <Redirect to={{ pathname: redirectionPath }} />;

const AuthenticatedRender = (allowedComponent, isLoggedIn, getUpdateUserInfo = false) => {
    const me = useSelector(meSlice);
    const dispatch = useDispatch();
    const [profileDetailsRetrieve, setProfileDetailsRetrieve] = useState(getUpdateUserInfo);
    useEffect(() => {
        if (!me || profileDetailsRetrieve) {
            dispatch({ type: `${SLICE_NAME}/getUserinfo` });
            setProfileDetailsRetrieve(false);
        }
    }, [me]);
    return !!me ? guardedRender(allowedComponent, isLoggedIn, '/login') : () => <CircularProgress />;
};

const UnauthenticatedRender = (allowedComponent, isLoggedIn) => guardedRender(allowedComponent, !isLoggedIn, '/');

// Specific guards.

// All canAccess functions should have arguments (userType, authorities) and return an object like:
// { isAllowed: boolean, redirectionPath: string }

const canAccessWithAuthority = (acceptedAuthorities, authorities) => {
    return {
        isAllowed: (acceptedAuthorities || []).filter((authority) => (authorities || '').includes(authority)).length > 0,
        redirectionPath: '/',
    };
};

// '/my-account'
const canAccessMyAccount = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_ACCOUNT_ADMINISTRATOR'], authorities);
};

// '/child-accounts/create' - '/child-accounts/edit'
const canAccessCreateOrEditChildAccount = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_ACCOUNT_ADMINISTRATOR'], authorities);
};

// '/online-services/:service'
const canAccessTrademarksOrDesigns = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_TRADEMARKS', 'ROLE_DESIGNS'], authorities);
};

const canAccessEdelivery = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_DESIGNS'], authorities);
};

const canAccessUpdateOwnerInfo = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_UPDATE_OWNER_INFORMATION'], authorities);
};

const canAccessImpersonateUsers = (_userType, authorities) => {
    return canAccessWithAuthority(['ROLE_IMPERSONATE_READ'], authorities);
};

export default {
    accessCheckRender,
    AuthenticatedRender,
    canAccessCreateOrEditChildAccount,
    canAccessMyAccount,
    canAccessTrademarksOrDesigns,
    UnauthenticatedRender,
    canAccessEdelivery,
    canAccessUpdateOwnerInfo,
    canAccessImpersonateUsers,
};
